import { faHands } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, useMemo } from 'react';
import { GoogleAdsLanguageGq } from '../../../graphql/projects/models/GoogleAdsLanguageGq';
import { languageList } from '../../../graphql/projects/models/languages';
import VoxelAutocomplete2, { VoxelAutocompleteProps } from '../../voxel/Forms/VoxelAutocomplete';
import VoxelText from '../../voxel/Text/VoxelText';

export interface VoxelAutocompleteLanguageProps
  extends Omit<
    VoxelAutocompleteProps,
    | 'setSelectedOptions'
    | 'options'
    | 'selectedOptionValues'
    | 'fixedOptions'
    | 'freeAdd'
    | 'multi'
    | 'placeholder'
    | 'label'
    | 'infoText'
  > {
  labelPosition?: 'top' | 'left';
  inputWidthClass?: string;
  selectedLanguage?: Partial<GoogleAdsLanguageGq>;
  setSelectedLanguage: (value?: Partial<GoogleAdsLanguageGq>) => void;
  disabled?: boolean;
  preText?: React.ReactNode;
  outlined?: boolean;
  helperText?: string;
  showHelperText?: boolean;
  type: 'keyword' | 'rank' | 'both' | 'user';
  isRequired?: boolean;
}

export const allLanguagesOption: GoogleAdsLanguageGq = {
  id: 1,
  name: 'All languages',
  code: 'dummy',
};

export const VoxelAutocompleteLanguage: React.FC<VoxelAutocompleteLanguageProps> = ({
  selectedLanguage,
  setSelectedLanguage,
  disabled,
  labelPosition,
  preText,
  outlined,
  helperText,
  showHelperText,
  inputWidthClass: widthClass,
  hasLock: editDisabledText,
  type,
  isRequired: requiredLabel,
  ...forwardedProps
}) => {
  const options = useMemo(
    () => languageList.map((lang) => ({ value: lang?.id, title: lang?.name })),
    [languageList]
  );

  const optionsWithNull = useMemo(
    () => [
      {
        value: allLanguagesOption.id,
        title: allLanguagesOption.name,
        icon: <FontAwesomeIcon icon={faHands} size="lg" className="-ml-0.5 text-voxel-gray-600" />,
      },
      ...options,
    ],
    [options]
  );

  const selectedOptionValues: number[] = useMemo(
    () => (selectedLanguage?.id ? [selectedLanguage?.id] : []),
    [selectedLanguage?.id]
  );

  const infoText: ReactElement = (
    <VoxelText div className="w-64 p-2 text-[13px]">
      Each keyword belongs to some set of languages.
      <br></br>
      <br></br>A keyword is included in the result if the selected language is one of its languages.
      If language is not set, keywords in all languages will be included in the result.
      <br></br>
      <br></br>
      By targeting 'All languages', you can reach people who speak more than one language and may
      search in several languages.
    </VoxelText>
  );
  return (
    <VoxelAutocomplete2
      {...forwardedProps}
      id="language"
      helperText={helperText}
      inputWidthClass={widthClass}
      showHelperText={showHelperText}
      outlined={outlined}
      label={`${type === 'rank' ? 'Rank Language' : 'Language'}`}
      isRequired={requiredLabel}
      preText={preText}
      labelPosition={labelPosition ?? 'left'}
      placeholder={'N/A'}
      options={type === 'rank' || type === 'user' ? options : optionsWithNull}
      selectedOptionValues={selectedOptionValues}
      emptyAllowed={false}
      setSelectedOptions={(options) => {
        const option = options[0];
        setSelectedLanguage(option ? { id: option.value, name: option.title } : undefined);
      }}
      disabled={disabled}
      hasLock={editDisabledText}
      infoText={type === 'keyword' && labelPosition === 'top' ? infoText : null}
    />
  );
};

export default VoxelAutocompleteLanguage;
